/**
 * Utility functions for extracting and managing concepts from TLDR data
 */

export interface KeyConcept {
  concept: string;
  definition: string;
}

export interface TLDRData {
  book_id?: string;
  page_number?: number;
  chapter_number?: number;
  chapter_name?: string;
  tldr?: {
    summary?: string;
    key_concepts?: KeyConcept[];
    practical_examples?: Array<{ concept: string; example: string }>;
    memory_aids?: Array<{ concept: string; technique: string }>;
    one_line_takeaway?: string;
  };
  concept_tags?: string[];
  key_topics?: string[];
  difficulty_level?: string;
  pedagogical_type?: string;
  processing_status?: string;
}

/**
 * Get a random concept from TLDR data
 * Randomly picks from key_concepts, then falls back to concept_tags, then key_topics
 */
export function getPrimaryConcept(tldrData: TLDRData | null): string | null {
  if (!tldrData) return null;

  // First priority: randomly pick from key_concepts
  const concepts = tldrData.tldr?.key_concepts;
  if (concepts && concepts.length > 0) {
    const randomIndex = Math.floor(Math.random() * concepts.length);
    return concepts[randomIndex].concept;
  }

  // Second priority: randomly pick from concept_tags
  if (tldrData.concept_tags && tldrData.concept_tags.length > 0) {
    const randomIndex = Math.floor(Math.random() * tldrData.concept_tags.length);
    return tldrData.concept_tags[randomIndex];
  }

  // Third priority: randomly pick from key_topics
  if (tldrData.key_topics && tldrData.key_topics.length > 0) {
    const randomIndex = Math.floor(Math.random() * tldrData.key_topics.length);
    return tldrData.key_topics[randomIndex];
  }

  return null;
}

/**
 * Get all concepts from TLDR data
 */
export function getAllConcepts(tldrData: TLDRData | null): string[] {
  if (!tldrData) return [];

  const concepts: string[] = [];

  // Add key_concepts
  if (tldrData.tldr?.key_concepts) {
    concepts.push(...tldrData.tldr.key_concepts.map(c => c.concept));
  }

  // Add concept_tags (dedupe)
  if (tldrData.concept_tags) {
    for (const tag of tldrData.concept_tags) {
      if (!concepts.includes(tag)) {
        concepts.push(tag);
      }
    }
  }

  return concepts;
}

/**
 * Get random concepts from visited pages
 * Used for session progress prompts
 */
export function getRandomConceptsFromPages(
  pages: number[],
  tldrCache: Map<number, TLDRData>,
  count: number = 3
): string[] {
  const allConcepts: string[] = [];

  for (const page of pages) {
    const data = tldrCache.get(page);
    if (data) {
      const concepts = getAllConcepts(data);
      allConcepts.push(...concepts);
    }
  }

  // Deduplicate
  const uniqueConcepts = Array.from(new Set(allConcepts));

  // Shuffle using Fisher-Yates algorithm
  for (let i = uniqueConcepts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [uniqueConcepts[i], uniqueConcepts[j]] = [uniqueConcepts[j], uniqueConcepts[i]];
  }

  return uniqueConcepts.slice(0, count);
}

/**
 * Pick random item from array
 */
export function pickRandom<T>(arr: T[]): T | undefined {
  if (arr.length === 0) return undefined;
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Pick random item from array with seed for deterministic results
 * Useful for generating consistent placeholder for same page
 */
export function pickRandomWithSeed<T>(arr: T[], seed: number): T | undefined {
  if (arr.length === 0) return undefined;
  // Simple seeded random using page number
  const index = seed % arr.length;
  return arr[index];
}
